import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  riskService,
  RiskAssessment,
  RiskAssessmentScope,
  RiskItem,
} from '../services/risk.service';
import { message } from 'antd';

export const riskKeys = {
  all: ['risk'] as const,
  riskAssessments: (filters?: any) => [...riskKeys.all, 'risk-assessments', filters] as const,
  riskAssessment: (id: string) => [...riskKeys.all, 'risk-assessment', id] as const,
};

export function useRiskAssessments(filters?: {
  project_id?: string;
  status?: string;
  scope?: RiskAssessmentScope;
}) {
  return useQuery({
    queryKey: riskKeys.riskAssessments(filters),
    queryFn: () => riskService.getRiskAssessments(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRiskAssessment(id: string) {
  return useQuery({
    queryKey: riskKeys.riskAssessment(id),
    queryFn: () => riskService.getRiskAssessmentById(id),
    enabled: !!id,
  });
}

export function useEnsureCompanyRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => riskService.ensureCompanyRegister(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.all });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to open company register'),
  });
}

export function useCreateRiskAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<RiskAssessment>) => riskService.createRiskAssessment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessments() });
      message.success('Risk assessment created successfully');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to create risk assessment'),
  });
}

export function useUpdateRiskAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RiskAssessment> }) =>
      riskService.updateRiskAssessment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessment(variables.id) });
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessments() });
      message.success('Risk assessment updated successfully');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to update risk assessment'),
  });
}

export function useApproveRiskAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      riskService.approveRiskAssessment(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessment(variables.id) });
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessments() });
      message.success('Risk assessment approved successfully');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to approve risk assessment'),
  });
}

export function useCreateRiskItem(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<RiskItem>) => riskService.createItem(assessmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessment(assessmentId) });
      message.success('Risk item added');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to add risk item'),
  });
}

export function useUpdateRiskItem(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Partial<RiskItem> }) =>
      riskService.updateItem(assessmentId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessment(assessmentId) });
      message.success('Risk item updated');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to update risk item'),
  });
}

export function useDeleteRiskItem(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => riskService.deleteItem(assessmentId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.riskAssessment(assessmentId) });
      message.success('Risk item deleted');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to delete risk item'),
  });
}

export function useSpawnProjectSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assessmentId,
      project_id,
      item_ids,
    }: {
      assessmentId: string;
      project_id: string;
      item_ids?: string[];
    }) => riskService.spawnProjectSnapshot(assessmentId, { project_id, item_ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.all });
      message.success('Project risk snapshot created');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to create project snapshot'),
  });
}
